import type {
  SprintMissionPoolSchema,
  MissionProgressSchema,
  PublicCampaignSchema,
} from "@/types";
import { campaignKey, type CampaignKey } from "@/campaigns/campaign-key";
import { toGameNames } from "@/campaigns/eligible-games";
import { assembleMissionRows } from "../missions/assemble";
import type { SprintMission } from "./types";

/**
 * Join the three streams into the rendered `SprintMission[]`. Pure — the store calls it (via
 * `derive.ts`) after any of them updates.
 *
 * **The subtype split.** This claims `subtype === "SM"` and `assembleMissions` claims
 * `subtype === "MI"`; the two must stay mutually exclusive, because `resolveCurrentCampaign`
 * returns the FIRST module whose `resolve` matches. If both claimed a campaign it would render
 * as a standard ladder, or twice.
 *
 * A campaign is included as soon as its `campaigns` tile arrives, before either volatile stream
 * lands — so the go-live tile is renderable, with `pool: null` meaning "counter not known yet"
 * rather than "no slots left".
 */
export function assembleSprintMissions(
  campaignsById: Record<CampaignKey, PublicCampaignSchema>,
  missionProgressById: Record<CampaignKey, MissionProgressSchema[]>,
  sprintMissionPoolById: Record<CampaignKey, SprintMissionPoolSchema>
): SprintMission[] {
  const result: SprintMission[] = [];

  for (const campaign of Object.values(campaignsById)) {
    if (campaign.type !== "MI" || campaign.subtype !== "SM") continue;

    const key = campaignKey(campaign);
    const missions = assembleMissionRows(
      key,
      campaign,
      missionProgressById[key] ?? []
    );
    const { games, count } = toGameNames(
      campaign.eligible_games,
      campaign.eligible_games_count
    );
    const mb = campaign.minimum_bet;
    const pool = sprintMissionPoolById[key];

    result.push({
      campaign,
      minimum_bet: mb
        ? { value: mb.value, currency: mb.currency, display: mb.display }
        : { value: 0, currency: "" },
      // The subtype guarantees exactly one mission (#3865 enforces it at publish and at the
      // write endpoints); `[0]` rather than the array is what removes the ladder from the UI.
      mission: missions[0] ?? null,
      eligible_games: games,
      eligible_games_count: count,
      pool: pool
        ? {
            prize_pool_size: pool.prize_pool_size,
            prizes_remaining: pool.prizes_remaining,
            exhausted: pool.exhausted,
            payout_timing: pool.payout_timing,
            // `?? []` guards a payload from a backend that predates the feed (#3958).
            recent_winners: pool.recent_winners ?? [],
          }
        : null,
    });
  }

  return result;
}
